<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
  } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    IBridgeDiscoveryCandidate,
    IBridgePrinter,
    IDiscoveryFinishedPayload,
    IDiscoveryFoundPayload,
  } from '@/interfaces/print-bridge-protocol';
  import {
    printerStatusUi,
    useV2PrintingUi,
  } from '@/composition/printing/use-printing-v2-ui';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { parseHostPort } from '@/lib/print/host-port';
  import { TWizardConnection } from './StepConnection.vue';

  export interface IWizardDevice {
    ref: string;
    name: string;
    transport: 'spooler' | 'tcp' | 'serial-bt';
    /** True when the wizard created the bridge entry (cancel may clean it up). */
    addedByWizard: boolean;
  }

  const props = defineProps<{
    connection: TWizardConnection;
  }>();

  const emit = defineEmits<{
    (e: 'select', device: IWizardDevice): void;
  }>();

  const ui = useUi();
  const { bridge, isConnected } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // #region USB (spooler list)

  const showVirtual = ref(false);
  const refreshing = ref(false);

  const spoolerPrinters = computed<IBridgePrinter[]>(() =>
    (bridge.value?.bridgePrinters.value ?? []).filter(
      (p) => p.transport === 'spooler'
    )
  );

  const visibleSpoolerPrinters = computed(() =>
    showVirtual.value
      ? spoolerPrinters.value
      : spoolerPrinters.value.filter((p) => !p.meta.isVirtual)
  );

  const hasVirtualPrinters = computed(() =>
    spoolerPrinters.value.some((p) => p.meta.isVirtual)
  );

  async function refreshSpooler() {
    if (!bridge.value) return;
    refreshing.value = true;
    try {
      await bridge.value.refreshPrinters();
    } catch (e) {
      console.error('[PrintingV2] printers.list failed', e);
    } finally {
      refreshing.value = false;
    }
  }

  function selectSpooler(printer: IBridgePrinter) {
    emit('select', {
      ref: printer.ref,
      name: printer.name,
      transport: 'spooler',
      addedByWizard: false,
    });
  }

  // #endregion

  // #region discovery (network + bluetooth)

  const searching = ref(false);
  const searchDone = ref(false);
  const candidates = ref<IBridgeDiscoveryCandidate[]>([]);
  let activeDiscoveryId: string | null = null;
  /**
   * Events can beat the discovery.start response (the id is not yet known):
   * they are buffered and replayed once the id arrives, so events from a
   * PREVIOUS scan can never leak into the current one.
   */
  let bufferedEvents: IBufferedDiscoveryEvent[] = [];
  const unsubscribers: (() => void)[] = [];

  type IBufferedDiscoveryEvent =
    | { type: 'found'; payload: IDiscoveryFoundPayload }
    | { type: 'finished'; payload: IDiscoveryFinishedPayload };

  function candidateKey(candidate: IBridgeDiscoveryCandidate): string {
    return candidate.transport === 'tcp'
      ? `tcp:${candidate.host}:${candidate.port ?? 9100}`
      : `bt:${candidate.comPort}`;
  }

  function handleDiscoveryFound(payload: IDiscoveryFoundPayload) {
    if (activeDiscoveryId === null) {
      if (searching.value) bufferedEvents.push({ type: 'found', payload });
      return;
    }
    if (payload.discoveryId !== activeDiscoveryId) return;
    const key = candidateKey(payload.printer);
    if (candidates.value.some((c) => candidateKey(c) === key)) return;
    candidates.value = [...candidates.value, payload.printer];
  }

  function handleDiscoveryFinished(payload: IDiscoveryFinishedPayload) {
    if (activeDiscoveryId === null) {
      if (searching.value) bufferedEvents.push({ type: 'finished', payload });
      return;
    }
    if (payload.discoveryId !== activeDiscoveryId) return;
    searching.value = false;
    searchDone.value = true;
  }

  async function startDiscovery() {
    if (!bridge.value || searching.value) return;
    candidates.value = [];
    searchDone.value = false;
    searching.value = true;
    activeDiscoveryId = null;
    bufferedEvents = [];
    try {
      const modes =
        props.connection === 'network'
          ? (['mdns', 'tcp-scan'] as const)
          : (['bt-ports'] as const);
      activeDiscoveryId = await bridge.value.startDiscovery([...modes]);
      const replay = bufferedEvents;
      bufferedEvents = [];
      for (const event of replay) {
        if (event.type === 'found') handleDiscoveryFound(event.payload);
        else handleDiscoveryFinished(event.payload);
      }
    } catch (e) {
      console.error('[PrintingV2] discovery.start failed', e);
      searching.value = false;
    }
  }

  const sortedCandidates = computed(() => {
    if (props.connection !== 'bluetooth') return candidates.value;
    // Likely-Bluetooth COM ports first.
    return [...candidates.value].sort(
      (a, b) => Number(!!b.likelyBluetooth) - Number(!!a.likelyBluetooth)
    );
  });

  // #endregion

  // #region manual add (candidate click + add-by-IP)

  const addingKey = ref<string | null>(null);

  async function selectCandidate(candidate: IBridgeDiscoveryCandidate) {
    const bridgeStrategy = bridge.value;
    if (!bridgeStrategy || addingKey.value) return;
    const key = candidateKey(candidate);
    addingKey.value = key;
    try {
      const printer =
        candidate.transport === 'tcp'
          ? await bridgeStrategy.addManualPrinter({
              transport: 'tcp',
              host: candidate.host ?? '',
              port: candidate.port ?? 9100,
              name: candidate.name,
              probe: true,
            })
          : await bridgeStrategy.addManualPrinter({
              transport: 'serial-bt',
              comPort: candidate.comPort ?? '',
              name: candidate.name,
              probe: true,
            });
      emit('select', {
        ref: printer.ref,
        name: printer.name,
        transport: candidate.transport,
        addedByWizard: true,
      });
    } catch (e) {
      console.error('[PrintingV2] printers.addManual failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.deviceAddFailed'));
    } finally {
      addingKey.value = null;
    }
  }

  const manualIp = ref('');
  const manualPort = ref('9100');
  const addingByIp = ref(false);
  const ipError = ref<string | null>(null);
  /** Set after a failed probe: enables "Continuar de todos modos". */
  const unreachableManual = ref<{ host: string; port: number } | null>(null);
  const addingUnreachable = ref(false);

  async function addByIp() {
    const bridgeStrategy = bridge.value;
    if (!bridgeStrategy || addingByIp.value) return;
    // Auto-splits a pasted "host:port" (printer self-test pages print the
    // address in that shape) — the pasted port wins over the Puerto field.
    const parsed = parseHostPort(manualIp.value, manualPort.value);
    if (!parsed.ok) {
      ipError.value = i18nT(
        parsed.error === 'host'
          ? 'printingV2.ipInvalid'
          : 'printingV2.portInvalid'
      );
      return;
    }
    ipError.value = null;
    unreachableManual.value = null;
    addingByIp.value = true;
    try {
      const printer = await bridgeStrategy.addManualPrinter({
        transport: 'tcp',
        host: parsed.host,
        port: parsed.port,
        probe: true,
      });
      emit('select', {
        ref: printer.ref,
        name: printer.name,
        transport: 'tcp',
        addedByWizard: true,
      });
    } catch (e) {
      console.error('[PrintingV2] add by IP failed', e);
      ipError.value = i18nT('printingV2.ipFailed');
      // Offer the escape hatch: some printers refuse the probe connection
      // pattern yet still print (the user may also power it on later).
      unreachableManual.value = { host: parsed.host, port: parsed.port };
    } finally {
      addingByIp.value = false;
    }
  }

  /** Escape hatch: adds the manual printer without probing it. */
  async function addUnreachableAnyway() {
    const bridgeStrategy = bridge.value;
    const target = unreachableManual.value;
    if (!bridgeStrategy || !target || addingUnreachable.value) return;
    addingUnreachable.value = true;
    try {
      const printer = await bridgeStrategy.addManualPrinter({
        transport: 'tcp',
        host: target.host,
        port: target.port,
        probe: false,
      });
      emit('select', {
        ref: printer.ref,
        name: printer.name,
        transport: 'tcp',
        addedByWizard: true,
      });
    } catch (e) {
      console.error('[PrintingV2] add without probe failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.deviceAddFailed'));
    } finally {
      addingUnreachable.value = false;
    }
  }

  // #endregion

  onMounted(() => {
    const bridgeStrategy = bridge.value;
    if (!bridgeStrategy) return;

    if (props.connection === 'usb') {
      void refreshSpooler();
      return;
    }

    unsubscribers.push(
      bridgeStrategy.onDiscoveryFound(handleDiscoveryFound),
      bridgeStrategy.onDiscoveryFinished(handleDiscoveryFinished)
    );
    void startDiscovery();
  });

  onBeforeUnmount(() => {
    for (const unsubscribe of unsubscribers) unsubscribe();
  });
</script>

<template>
  <div class="wizard-step-device">
    <!-- USB: installed printers -->
    <template v-if="connection === 'usb'">
      <div class="device-toolbar">
        <label v-if="hasVirtualPrinters" class="device-virtual-toggle">
          <input v-model="showVirtual" type="checkbox" />
          {{ $t('printingV2.showVirtual') }}
        </label>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="refresh"
          :loading="refreshing"
          :disabled="!isConnected"
          @click="refreshSpooler"
        >
          {{ $t('printingV2.refresh') }}
        </PosUiButton>
      </div>

      <div v-if="!visibleSpoolerPrinters.length" class="device-empty">
        {{ $t('printingV2.deviceUsbEmpty') }}
      </div>

      <div v-else class="device-list">
        <button
          v-for="printer in visibleSpoolerPrinters"
          :key="printer.ref"
          type="button"
          class="device-row"
          @click="selectSpooler(printer)"
        >
          <PosUiIcon name="usb" class="device-row-icon" />
          <span class="device-row-name">{{ printer.name }}</span>
          <PosUiBadge
            :color="printerStatusUi(printer).color"
            variant="soft"
            size="xs"
          >
            {{ $t(printerStatusUi(printer).labelKey) }}
          </PosUiBadge>
          <PosUiIcon name="chevron-right" class="device-row-chevron" />
        </button>
      </div>
    </template>

    <!-- Network / Bluetooth: discovery -->
    <template v-else>
      <div class="device-toolbar">
        <div v-if="searching" class="device-searching">
          <span class="device-searching-spinner" />
          {{ $t('printingV2.searching') }}
        </div>
        <div v-else-if="searchDone" class="device-search-done">
          {{ $t('printingV2.searchFinished') }}
        </div>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="refresh"
          :loading="searching"
          :disabled="!isConnected"
          @click="startDiscovery"
        >
          {{ $t('printingV2.refresh') }}
        </PosUiButton>
      </div>

      <div v-if="sortedCandidates.length" class="device-list">
        <button
          v-for="candidate in sortedCandidates"
          :key="candidateKey(candidate)"
          type="button"
          class="device-row"
          :disabled="!!addingKey"
          @click="selectCandidate(candidate)"
        >
          <PosUiIcon
            :name="candidate.transport === 'tcp' ? 'lan' : 'bluetooth'"
            class="device-row-icon"
          />
          <span class="device-row-name">
            {{ candidate.name }}
            <span
              v-if="candidate.transport === 'tcp'"
              class="device-row-detail"
            >
              {{ candidate.host }}:{{ candidate.port ?? 9100 }}
            </span>
            <span v-else class="device-row-detail">{{
              candidate.comPort
            }}</span>
          </span>
          <span
            v-if="addingKey === candidateKey(candidate)"
            class="device-row-spinner"
          />
          <PosUiIcon v-else name="chevron-right" class="device-row-chevron" />
        </button>
      </div>
      <div v-else-if="searchDone" class="device-empty">
        {{ $t('printingV2.noResultsFound') }}
      </div>

      <!-- Add by IP (network only) -->
      <div v-if="connection === 'network'" class="device-add-by-ip">
        <div class="add-by-ip-title">{{ $t('printingV2.addByIp') }}</div>
        <div class="add-by-ip-row">
          <PosUiInput
            :value="manualIp"
            :placeholder="$t('printingV2.ipPlaceholder')"
            inputmode="decimal"
            class="add-by-ip-input"
            @input="
              manualIp = $event;
              ipError = null;
              unreachableManual = null;
            "
            @enter="addByIp"
          />
          <PosUiInput
            :value="manualPort"
            :placeholder="$t('printingV2.portPlaceholder')"
            inputmode="numeric"
            class="add-by-ip-port"
            @input="
              manualPort = $event;
              ipError = null;
              unreachableManual = null;
            "
            @enter="addByIp"
          />
          <PosUiButton
            color="primary"
            variant="soft"
            :loading="addingByIp"
            :disabled="!isConnected || !manualIp.trim()"
            @click="addByIp"
          >
            {{ $t('printingV2.ipAdd') }}
          </PosUiButton>
        </div>
        <div v-if="ipError" class="add-by-ip-error">{{ ipError }}</div>
        <div v-if="unreachableManual" class="add-by-ip-unreachable">
          <PosUiIcon name="alert" class="unreachable-icon" />
          <div class="unreachable-body">
            <div class="unreachable-text">
              {{ $t('printingV2.deviceUnreachableWarn') }}
            </div>
            <PosUiButton
              size="sm"
              color="warning"
              variant="soft"
              :loading="addingUnreachable"
              @click="addUnreachableAnyway"
            >
              {{ $t('printingV2.continueAnyway') }}
            </PosUiButton>
          </div>
        </div>
      </div>

      <!-- Bluetooth pairing guide -->
      <div v-if="connection === 'bluetooth'" class="device-bt-guide">
        <div class="bt-guide-title">{{ $t('printingV2.btGuideTitle') }}</div>
        <ol class="bt-guide-steps">
          <li>{{ $t('printingV2.btGuide1') }}</li>
          <li>{{ $t('printingV2.btGuide2') }}</li>
          <li>{{ $t('printingV2.btGuide3') }}</li>
        </ol>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .wizard-step-device {
    @apply tw-flex tw-flex-col tw-gap-3;

    .device-toolbar {
      @apply tw-flex tw-min-h-[36px] tw-items-center tw-justify-between tw-gap-2;

      .device-virtual-toggle {
        @apply tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-text-sm tw-text-neutral-600;
      }

      .device-searching {
        @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-500;

        .device-searching-spinner {
          @apply tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-neutral-300 tw-border-t-orange-500;
        }
      }

      .device-search-done {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .device-empty {
      @apply tw-rounded-lg tw-bg-neutral-50 tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-neutral-500;
    }

    .device-list {
      @apply tw-flex tw-max-h-[280px] tw-flex-col tw-gap-1.5 tw-overflow-y-auto;

      .device-row {
        @apply tw-flex tw-min-h-[52px] tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-left tw-transition-colors hover:tw-border-orange-300 hover:tw-bg-orange-50/40;

        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-60;
        }

        .device-row-icon {
          @apply tw-flex-none tw-text-xl tw-text-neutral-400;
        }

        .device-row-name {
          @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-neutral-800;

          .device-row-detail {
            @apply tw-ml-1.5 tw-text-xs tw-font-normal tw-text-neutral-400;
          }
        }

        .device-row-chevron {
          @apply tw-flex-none tw-text-lg tw-text-neutral-300;
        }

        .device-row-spinner {
          @apply tw-h-4 tw-w-4 tw-flex-none tw-animate-spin tw-rounded-full tw-border-2 tw-border-neutral-300 tw-border-t-orange-500;
        }
      }
    }

    .device-add-by-ip {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3.5;

      .add-by-ip-title {
        @apply tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .add-by-ip-row {
        @apply tw-flex tw-items-center tw-gap-2;

        .add-by-ip-input {
          @apply tw-max-w-[240px];
        }

        .add-by-ip-port {
          @apply tw-max-w-[96px];
        }
      }

      .add-by-ip-error {
        @apply tw-mt-1.5 tw-text-xs tw-text-red-600;
      }

      .add-by-ip-unreachable {
        @apply tw-mt-2.5 tw-flex tw-items-start tw-gap-2.5 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3;

        .unreachable-icon {
          @apply tw-mt-0.5 tw-flex-none tw-text-lg tw-text-amber-500;
        }

        .unreachable-body {
          @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-gap-2;

          .unreachable-text {
            @apply tw-text-sm tw-text-amber-800;
          }
        }
      }
    }

    .device-bt-guide {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3.5;

      .bt-guide-title {
        @apply tw-mb-2 tw-text-sm tw-font-semibold tw-text-neutral-700;
      }

      .bt-guide-steps {
        @apply tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-text-neutral-600;
      }
    }
  }
</style>
